import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Fab,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";

import PageBanner from "../../components/PageBanner";
import PageContent from "../../components/PageContent";
import {
  deleteQuestionnaire,
  fetchQuestionnaireDatagridRows,
} from "../../services/questionnaireService";
import { AppConfirmDialog, AppToast } from "../../components/AppFeedback";
import DatagridNavLink from "../../components/DatagridNavLink";
import { useUserContext } from "../../context/UserContext";
import QuestionnaireCreateForm from "./QuestionnaireCreateForm";

const QuestionnaireList = () => {
  const navigate = useNavigate();
  const { roles = [] } = useUserContext();
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [sortModel, setSortModel] = useState([{ field: "qid", sort: "desc" }]);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteQid, setDeleteQid] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const canManageQuestionnaires = roles.some((role) => {
    const upper = String(role).toUpperCase();
    return upper === "ADMIN" || upper === "MANAGER";
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput.trim());
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 400);

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      setLoading(true);
      setError("");
      try {
        const sort = sortModel[0] || {};
        const apiSortFieldMap = {
          qid: "qid",
          name: "name",
          label: "label",
        };

        const response = await fetchQuestionnaireDatagridRows({
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          search: searchTerm,
          sortField: apiSortFieldMap[sort.field] || "qid",
          sortOrder: sort.sort || "desc",
        });

        if (cancelled) return;

        setRows(response.rows || []);
        setRowCount(response.rowCount || 0);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Failed to load questionnaires");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadData();
    return () => {
      cancelled = true;
    };
  }, [paginationModel, sortModel, searchTerm]);

  const handleDelete = async () => {
    if (!deleteQid) return;

    try {
      const sort = sortModel[0] || {};
      const apiSortFieldMap = {
        qid: "qid",
        name: "name",
        label: "label",
      };

      await deleteQuestionnaire(deleteQid);
      setSnackbar({
        open: true,
        message: "Questionnaire deleted successfully",
        severity: "success",
      });
      setDeleteQid(null);

      // Refresh current page after delete
      const response = await fetchQuestionnaireDatagridRows({
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
        search: searchTerm,
        sortField: apiSortFieldMap[sort.field] || "qid",
        sortOrder: sort.sort || "desc",
      });
      setRows(response.rows || []);
      setRowCount(response.rowCount || 0);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || "Failed to delete questionnaire",
        severity: "error",
      });
      setDeleteQid(null);
    }
  };

  const handleCreateComplete = async () => {
    // Refresh data after successful creation
    try {
      const sort = sortModel[0] || {};
      const apiSortFieldMap = {
        qid: "qid",
        name: "name",
        label: "label",
        timeline_count: "timeline_count",
      };
      const response = await fetchQuestionnaireDatagridRows({
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
        search: searchTerm,
        sortField: apiSortFieldMap[sort.field] || "qid",
        sortOrder: sort.sort || "desc",
      });
      setRows(response.rows || []);
      setRowCount(response.rowCount || 0);
    } catch (err) {
      console.error("Failed to refresh data after creation:", err);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "qid",
        headerName: "Questionnaire ID",
        flex: 1.2,
        minWidth: 200,
        renderCell: (params) => (
          <DatagridNavLink
            to={params.row.qid ? `/questionnaire-management/edit/${params.row.qid}` : ""}
          >
            {params.value}
          </DatagridNavLink>
        ),
      },
      {
        field: "name",
        headerName: "Name",
        flex: 1,
        minWidth: 150,
        renderCell: (params) => (
          <DatagridNavLink
            to={params.row.qid ? `/questionnaire-management/edit/${params.row.qid}` : ""}
          >
            {params.value}
          </DatagridNavLink>
        ),
      },
      {
        field: "label",
        headerName: "Label (Display Name)",
        flex: 1.4,
        minWidth: 180,
      },
      {
        field: "actions",
        headerName: "Actions",
        minWidth: 130,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Tooltip title="Edit">
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={(event) => {
                    event.stopPropagation();
                    navigate(`/questionnaire-management/edit/${params.row.qid}`);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete">
              <span>
                <IconButton
                  size="small"
                  color="error"
                  disabled={!canManageQuestionnaires}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (!canManageQuestionnaires) return;
                    setDeleteQid(params.row.qid);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [canManageQuestionnaires, navigate]
  );

  return (
    <Box sx={{ bgcolor: "#dde7ecff", minHeight: "100vh" }}>
      <PageBanner title="Questionnaires" />

      <PageContent width={{ xs: "100%", sm: "95%", md: "85%", lg: "80%" }}>
        <Box sx={{ borderRadius: 1, p: { xs: 1.5, sm: 2 }, bgcolor: "#ffffff" }}>
          <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
            <TextField
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search by name or label"
              size="small"
              sx={{ width: { xs: "100%", sm: 320 } }}
            />
          </Box>

          {error ? <Alert severity="error">{error}</Alert> : null}

          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.qid}
            loading={loading}
            pagination
            paginationMode="server"
            sortingMode="server"
            rowCount={rowCount}
            pageSizeOptions={[10, 20, 50]}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            disableRowSelectionOnClick
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? "row-even" : "row-odd"
            }
            autoHeight
            sx={{
              "& .row-even": {
                backgroundColor: "#ffffff",
              },
              "& .row-odd": {
                backgroundColor: "#f5f5f5",
              },
            }}
          />
        </Box>
      </PageContent>

      <AppConfirmDialog
        open={Boolean(deleteQid)}
        title="Confirm Deletion"
        content={
          <>
            Are you sure you want to delete questionnaire <strong>{deleteQid}</strong>?
            <br />
            <span style={{ color: "#d32f2f", fontSize: "0.875rem", marginTop: "8px", display: "block" }}>
              ⚠️ Make sure it's not attached to any project timeline.
            </span>
          </>
        }
        confirmLabel="Delete"
        confirmColor="error"
        onCancel={() => setDeleteQid(null)}
        onConfirm={handleDelete}
      />

      <AppToast
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />

      <QuestionnaireCreateForm
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreateComplete}
      />

      <Tooltip
        title={
          <Typography sx={{ fontSize: "1rem", fontWeight: "bold" }}>
            Add Questionnaire
          </Typography>
        }
      >
        <Fab
          color="primary"
          aria-label="add-questionnaire"
          onClick={() => {
            if (!canManageQuestionnaires) {
              setSnackbar({
                open: true,
                message: "Only admin or manager can create questionnaires",
                severity: "warning",
              });
              return;
            }
            setCreateOpen(true);
          }}
          sx={{
            position: "fixed",
            bottom: 32,
            right: 32,
            zIndex: 1000,
            bgcolor: "#1a245a",
            "&:hover": {
              backgroundColor: "#3a4a8e",
            },
          }}
        >
          <AddIcon />
        </Fab>
      </Tooltip>
    </Box>
  );
};

export default QuestionnaireList;
